import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { CastFigure, MOTION_TIMING, useAutoplayScroll, useReducedMotion, useVideoKeepAlive } from '../../motion';
import draftGrades from '../../data/draftGrades.json';
import { pickDaily, FILM_END_CARDS } from '../../utils/rotatingCopy';
import './film-home.css';

// The post-draft film. Six pinned beats, one rAF scroll loop, and one v2.1 grading source.
// Draft figures and counts are always read from draftGrades.json, shared with The Registrar.

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const ease = (value) => 0.5 - (0.5 * Math.cos(Math.PI * clamp(value)));
const seg = (progress, start, end) => clamp((progress - start) / (end - start));
const lerp = (start, end, progress) => start + ((end - start) * progress);

const PICKS = draftGrades.picks;
const DRAFTED_PICKS = PICKS.filter((pick) => !pick.keeper);
const KEEPER_PICKS = PICKS.filter((pick) => pick.keeper);
const TEAMS = draftGrades.teams;
const GRADE_TEAMS = [...TEAMS].sort((a, b) => b.rank - a.rank);
const ROUND_NUMBERS = [...new Set(PICKS.map((pick) => pick.round))].sort((a, b) => a - b);
const ROUND_GROUPS = ROUND_NUMBERS.map((round) => ({
  round,
  picks: PICKS.filter((pick) => pick.round === round),
}));
const ROUND_COLUMNS = [ROUND_GROUPS.slice(0, 9), ROUND_GROUPS.slice(9)];

const teamByManager = (manager) => TEAMS.find((team) => team.manager === manager);
const teamByFantasyTeam = (teamName) => TEAMS.find((team) => team.team === teamName);
const keeperForTeam = (team) => team.keeperVerdict[0];
const oneDecimal = (value) => Number(value).toFixed(1);
const signed = (value) => `${value > 0 ? '+' : value < 0 ? '−' : ''}${oneDecimal(Math.abs(value))}`;
const signedRounded = (value) => `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.round(Math.abs(value))}`;
const SMALL_NUMBERS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function numberWords(value) {
  const number = Math.trunc(Number(value));
  if (number < 20) return SMALL_NUMBERS[number];
  if (number < 100) return `${TENS[Math.floor(number / 10)]}${number % 10 ? `-${SMALL_NUMBERS[number % 10]}` : ''}`;
  if (number < 1000) return `${SMALL_NUMBERS[Math.floor(number / 100)]} hundred${number % 100 ? ` and ${numberWords(number % 100)}` : ''}`;
  return String(number);
}

const sentenceCase = (value) => value.charAt(0).toUpperCase() + value.slice(1);
const decimalWords = (value, precision = 1) => {
  const [whole, decimal] = Number(value).toFixed(precision).split('.');
  return `${numberWords(whole)} point ${decimal.split('').map((digit) => SMALL_NUMBERS[Number(digit)]).join(' ')}`;
};

const GRADE_CARD_CONFIG = {
  'a-j': { pose: 'facepalm', verdict: (team) => `Hosted. Bench quality: ${oneDecimal(team.benchQuality)}.` },
  josh: { pose: 'facepalm', verdict: (team) => `Keeper surplus ${signed(keeperForTeam(team).keeperSurplus)}. Starter score: ${oneDecimal(team.componentScores.starterStrength)}.` },
  robby: { pose: 'smug', verdict: (team) => `Kept a tight end in round ${numberWords(keeperForTeam(team).round)}.` },
  amir: { pose: 'shock', verdict: (team) => `Round ${team.worstPick.round}: ${team.worstPick.player}, ${signed(team.worstPick.valueVsSlot)}.` },
  bryan: { pose: 'facepalm', verdict: (team) => `${sentenceCase(numberWords(team.byePileups[0].count))} starters, ${numberWords(team.byePileups.length)} bye week.` },
  anna: { pose: 'base', verdict: (team) => `Keeper priced at overall ${keeperForTeam(team).keeperCostOverall}: ${signed(keeperForTeam(team).keeperSurplus)}.` },
  samuel: { pose: 'point', verdict: (team) => `Late rounds: ${signed(team.roundEfficiency.splits.lateRounds12to17)}.` },
  ryan: { pose: 'point', verdict: (team) => `Leveraged efficiency: ${signed(team.draftedEfficiencyLeveraged)}. Margin risk: ${oneDecimal(team.thinnestMargin.score)}.` },
  kyle: { pose: 'smug', verdict: (team) => `Rank ${team.rank}. Bench quality: ${oneDecimal(team.benchQuality)}.` },
  kevin: {
    pose: 'celebrate',
    verdict: (team) => {
      const openingRoundPicks = PICKS.filter((pick) => pick.manager === team.manager && pick.round === ROUND_NUMBERS[0]).length;
      return `He drafted ${openingRoundPicks === 2 ? 'twice' : `${numberWords(openingRoundPicks)} times`} in round ${numberWords(ROUND_NUMBERS[0])}. One was Robby's.`;
    },
  },
};

function boardAnnotation(pick) {
  if (pick.overall === draftGrades.superlatives.stealOfDraft.overall) {
    return `Steal of the draft. ${pick.manager}, ${signed(pick.valueVsSlot)} against the slot.`;
  }
  if (pick.overall === draftGrades.superlatives.biggestReach.overall) {
    return `The reach. ${pick.manager}, ${signed(pick.valueVsSlot)} against the slot.`;
  }
  if (pick.overall === 1) {
    return `Keeper. First overall, priced at first overall. ${signed(pick.keeperSurplus)} surplus.`;
  }
  if (pick.player === keeperForTeam(teamByManager('Ryan')).player) {
    return `Keeper. ${signed(pick.keeperSurplus)} surplus. Ryan’s best decision was made last September.`;
  }
  if (pick.player === keeperForTeam(teamByManager('Robby')).player) {
    return `Keeper. ${signed(pick.keeperSurplus)} surplus. He announced he was keeping nobody. Then this.`;
  }
  if (pick.overall === PICKS.length) {
    return `Pick ${pick.overall}. The board closes.`;
  }
  return null;
}

const ANNOTATED_PICKS = new Set(PICKS.filter(boardAnnotation).map((pick) => pick.overall));

const EXHIBITS = (() => {
  const robby = teamByManager('Robby');
  const aj = teamByManager('A.J.');
  const bestKeeper = draftGrades.superlatives.bestKeeperDeal;
  const reach = draftGrades.superlatives.biggestReach;
  const bestKeeperTeam = teamByFantasyTeam(bestKeeper.fantasyTeam);
  const reachTeam = teamByFantasyTeam(reach.fantasyTeam);
  const robbyKeeper = keeperForTeam(robby);
  const robbyKeeperPick = KEEPER_PICKS.find((pick) => pick.player === robbyKeeper.player);
  return [
    {
      tag: 'Exhibit A · Best keeper deal',
      name: bestKeeperTeam.manager,
      num: signedRounded(bestKeeper.keeperSurplus),
      tone: 'gold',
      copy: `${bestKeeper.player}, held from round ${bestKeeper.round}. The best decision on a board that graded ${bestKeeperTeam.letter}.`,
      pose: 'celebrate',
    },
    {
      tag: 'Exhibit B · Biggest reach',
      name: reachTeam.manager,
      num: signedRounded(reach.valueVsSlot),
      tone: 'red',
      copy: `${reach.player} at overall ${reach.overall}, round ${reach.round}. The file's best drafted pick was ${reachTeam.bestPick.player}.`,
      pose: 'shock',
    },
    {
      tag: 'Exhibit C · The tenth keeper',
      name: robby.manager,
      num: `R${robbyKeeper.round} · ${signedRounded(robbyKeeper.keeperSurplus)}`,
      tone: 'gold',
      copy: `He said he was keeping nobody. ${robbyKeeper.player} was priced to him at overall ${robbyKeeperPick.overall} anyway.`,
      pose: 'smug',
    },
    {
      tag: 'Exhibit D · Bench quality',
      name: aj.manager,
      num: oneDecimal(aj.benchQuality),
      tone: 'red',
      copy: 'The host finished with the room’s thinnest bench. Measured, not alleged.',
      pose: 'facepalm',
    },
  ];
})();

const AJ = teamByManager('A.J.');
const CLOSEUP_LINES = [
  {
    big: 'He hosted.',
    small: `${sentenceCase(numberWords(ROUND_NUMBERS.length))} rounds, in the dining room of a brand-new build.`,
    pose: 'base',
  },
  {
    big: `${sentenceCase(decimalWords(AJ.benchQuality))}.`,
    small: 'Bench quality. Not a rounding error.',
    pose: 'shock',
  },
  {
    big: `${AJ.bestPick.player} was fine.`,
    small: `${signed(AJ.bestPick.valueVsSlot)} against the slot. Last thing that worked.`,
    pose: 'point',
  },
  {
    big: `Then round ${numberWords(AJ.worstPick.round)}.`,
    small: `${AJ.worstPick.player}. ${signed(AJ.worstPick.valueVsSlot)} against the slot.`,
    pose: 'facepalm',
  },
  {
    big: '“Third,” he said.',
    small: 'That was before the draft.',
    pose: 'smug',
  },
];

function offsetWithin(element, ancestor) {
  let top = 0;
  let node = element;
  while (node && node !== ancestor) {
    top += node.offsetTop;
    node = node.offsetParent;
  }
  return top;
}

function nearestItem(items, target) {
  if (!items.length) return null;
  let low = 0;
  let high = items.length - 1;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (items[mid].center < target) low = mid + 1;
    else high = mid;
  }
  const after = items[low];
  const before = items[Math.max(0, low - 1)];
  return Math.abs(before.center - target) <= Math.abs(after.center - target) ? before : after;
}

function GradeCard({ team, pose }) {
  const config = GRADE_CARD_CONFIG[team.slug];
  return (
    <li
      className="fh-grade-card paper"
      data-tier={team.tier}
      style={{ '--sr': `${(team.rank - 5.5) * -4}deg` }}
      aria-label={`${team.manager}, ${team.letter}, ${team.score.toFixed(2)}. ${config.verdict(team)}`}
    >
      <CastFigure name={team.manager} trigger="none" pose={pose} className="fh-grade-card__fig" preload={false} alt={`${team.manager} caricature`} />
      <div className="fh-grade-card__face">
        <span className="fh-grade-card__letter">{team.letter}</span>
        <span className="fh-grade-card__score">{team.score.toFixed(2)}</span>
        <b>{team.manager === 'A.J.' ? 'AJ' : team.manager}</b>
        <small>{team.team}</small>
        <em>{config.verdict(team)}</em>
      </div>
    </li>
  );
}

export default function HomeFilm({ skipTarget = '#almanac' }) {
  const reduced = useReducedMotion();
  const root = useRef(null);
  const beats = useRef({});
  const coronationVideoRef = useRef(null);
  const endVideoRef = useRef(null);
  const initialGradePoses = useMemo(() => Object.fromEntries(
    GRADE_TEAMS.map((team) => [team.manager, reduced ? (team.manager === 'Kevin' ? 'smug' : GRADE_CARD_CONFIG[team.slug].pose) : 'base'])
  ), [reduced]);
  const [gradePoses, setGradePoses] = useState(initialGradePoses);
  const [closeupPose, setCloseupPose] = useState(reduced ? 'smug' : 'base');
  const endCard = pickDaily(FILM_END_CARDS);
  useVideoKeepAlive(coronationVideoRef, { enabled: !reduced });
  useVideoKeepAlive(endVideoRef, { enabled: !reduced });

  const getBounds = useCallback(() => {
    const element = root.current;
    if (!element) return { from: 0, to: 0 };
    const top = element.getBoundingClientRect().top + window.scrollY;
    return { from: top, to: top + element.offsetHeight - window.innerHeight };
  }, []);

  // Automation gets the same scroll-driven film without moving on its own.
  // `?film=1` keeps the autoplay preview path available.
  const automated = typeof navigator !== 'undefined' && navigator.webdriver === true
    && !/[?&]film=1(&|$)/.test(typeof window !== 'undefined' ? window.location.search : '');
  const { state, play, pause } = useAutoplayScroll({
    duration: MOTION_TIMING.homeFilmDurationMs,
    startDelay: MOTION_TIMING.homeFilmStartDelayMs,
    enabled: !reduced && !automated,
    getBounds,
  });

  const skip = () => {
    pause();
    const target = document.querySelector(skipTarget);
    if (target) target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (reduced) return undefined;
    const query = (selector, scope = root.current) => scope?.querySelector(selector);
    const queryAll = (selector, scope = root.current) => [...(scope?.querySelectorAll(selector) || [])];
    const gradeCards = queryAll('.fh-grade-card');
    const roundEls = queryAll('.fh-round');
    const exhibitEls = queryAll('.fh-exhibit');
    const closeupLines = queryAll('.fh-closeup__line');
    const boardBeat = beats.current.board;
    const boardStage = query('.fh-stage', boardBeat);
    const boardWall = query('.fh-board-wall', boardBeat);
    let boardMetrics = {
      mobile: false,
      height: 0,
      wallHeight: 0,
      columns: [[], []],
      annotatedColumns: [[], []],
      all: [],
      annotated: [],
    };
    let lastGradePoses = {};
    let lastCloseupPose = '';
    let litBoardItem = null;
    let ticking = false;

    const measureBoard = () => {
      if (!boardWall || !boardStage) return;
      const mobile = window.innerWidth <= 900;
      const items = queryAll('.fh-slip', boardWall).map((element) => ({
        element,
        column: Number(element.dataset.column),
        center: offsetWithin(element, boardWall) + (element.offsetHeight / 2),
        annotated: element.classList.contains('is-annotated'),
      }));
      const columns = [items.filter((item) => item.column === 0), items.filter((item) => item.column === 1)];
      boardMetrics = {
        mobile,
        height: boardStage.clientHeight,
        wallHeight: boardWall.scrollHeight,
        columns,
        annotatedColumns: columns.map((column) => column.filter((item) => item.annotated)),
        all: items.sort((a, b) => a.center - b.center),
        annotated: items.filter((item) => item.annotated).sort((a, b) => a.center - b.center),
      };
    };

    const applyGradePoses = (next) => {
      const changed = Object.keys(next).some((key) => next[key] !== lastGradePoses[key])
        || Object.keys(lastGradePoses).length !== Object.keys(next).length;
      if (changed) {
        lastGradePoses = next;
        setGradePoses(next);
      }
    };

    const applyCloseupPose = (pose) => {
      if (pose !== lastCloseupPose) {
        lastCloseupPose = pose;
        setCloseupPose(pose);
      }
    };

    const functions = {
      room(progress) {
        const beat = beats.current.room;
        const bg = query('.fh-room__bg', beat);
        const fore = query('.fh-room__fore', beat);
        const grade = query('.fh-room__grade', beat);
        const live = query('.fh-open__live', beat);
        const rule = query('.fh-open__rule', beat);
        const mark = query('.fh-open__mark', beat);
        const sub = query('.fh-open__sub', beat);
        const tag = query('.fh-open__tag', beat);
        const cue = query('.fh-open__cue', beat);
        const wrap = query('.fh-open', beat);
        if (bg) bg.style.transform = `translateX(${lerp(2, -1, progress)}%) scale(${lerp(1.18, 1.02, progress)})`;
        if (fore) fore.style.transform = `translateX(${lerp(3.2, -1.6, progress)}%) scale(${lerp(1.34, 1.05, progress)})`;
        if (grade) grade.style.opacity = lerp(1, 0.72, seg(progress, 0, 0.3));
        if (live) live.style.opacity = ease(seg(progress, 0.03, 0.12));
        if (rule) rule.style.transform = `scaleX(${ease(seg(progress, 0.55, 0.7))})`;
        const markIn = ease(seg(progress, 0.1, 0.28));
        if (mark) {
          mark.style.opacity = markIn;
          mark.style.transform = `scale(${lerp(1.35, 1, markIn)})`;
          mark.style.filter = `blur(${lerp(14, 0, markIn)}px)`;
          mark.style.letterSpacing = `${lerp(0.16, 0.04, markIn)}em`;
        }
        const subIn = ease(seg(progress, 0.3, 0.42));
        if (sub) {
          sub.style.opacity = subIn;
          sub.style.transform = `translateY(${lerp(18, 0, subIn)}px)`;
        }
        if (tag) tag.style.opacity = ease(seg(progress, 0.46, 0.58));
        if (cue) cue.style.opacity = 1 - ease(seg(progress, 0.05, 0.14));
        const exit = ease(seg(progress, 0.82, 1));
        if (wrap) {
          wrap.style.transform = `scale(${lerp(1, 1.08, exit)})`;
          wrap.style.opacity = 1 - exit;
        }
      },
      board(progress) {
        if (!boardStage || !boardWall) return;
        const copy = query('.fh-board__copy', boardBeat);
        const copyIn = ease(seg(progress, 0, 0.1));
        if (copy) {
          copy.style.opacity = copyIn;
          copy.style.transform = `translateY(${lerp(14, 0, copyIn)}px)`;
        }
        roundEls.forEach((round, index) => {
          const enter = ease(seg(progress, index * 0.009, 0.07 + (index * 0.009)));
          round.style.opacity = enter;
          round.style.transform = `translateY(${lerp(18, 0, enter)}px)`;
        });

        const height = boardMetrics.height || boardStage.clientHeight;
        const focal = height * 0.56;
        let activeItems = boardMetrics.all;
        let annotatedItems = boardMetrics.annotated;
        let translateY = height * 0.28;
        if (boardMetrics.mobile) {
          const travel = Math.max(0, boardMetrics.wallHeight - (height * 0.55));
          translateY = lerp(height * 0.28, -travel, ease(seg(progress, 0.22, 0.94)));
        } else {
          const rightSide = progress >= 0.59;
          const columnProgress = rightSide ? seg(progress, 0.59, 0.94) : seg(progress, 0.22, 0.57);
          activeItems = boardMetrics.columns[rightSide ? 1 : 0];
          annotatedItems = boardMetrics.annotatedColumns[rightSide ? 1 : 0];
          const lastCenter = activeItems[activeItems.length - 1]?.center || 0;
          const firstCenter = activeItems[0]?.center || 0;
          translateY = lerp(focal - firstCenter, focal - lastCenter, ease(columnProgress));
        }
        boardWall.style.setProperty('--dolly-y', `${translateY}px`);
        const nearest = nearestItem(annotatedItems, focal - translateY);
        if (nearest?.element !== litBoardItem) {
          litBoardItem?.classList.remove('is-lit');
          nearest?.element.classList.add('is-lit');
          litBoardItem = nearest?.element || null;
        }
        boardStage.style.setProperty('--bd-s', lerp(1.12, 1, progress));
      },
      grades(progress) {
        const beat = beats.current.grades;
        const head = query('.fh-grades__head', beat);
        const folder = query('.fh-folder', beat);
        const flap = query('.fh-folder__flap', beat);
        const glow = query('.fh-folder__glow', beat);
        const grid = query('.fh-grades__grid', beat);
        const headIn = ease(seg(progress, 0, 0.11));
        if (head) {
          head.style.opacity = headIn;
          head.style.transform = `translateY(${lerp(14, 0, headIn)}px)`;
        }
        const folderIn = ease(seg(progress, 0, 0.1));
        const folderOut = ease(seg(progress, 0.3, 0.86));
        if (folder) {
          folder.style.opacity = 1 - folderOut;
          folder.style.transform = `translate3d(0, ${lerp(18, 0, folderIn)}px, ${lerp(0, -400, folderOut)}px) scale(${lerp(0.86, 1, folderIn) * lerp(1, 0.7, folderOut)})`;
        }
        if (flap) flap.style.setProperty('--flap', `${lerp(0, -104, ease(seg(progress, 0.1, 0.2)))}deg`);
        if (glow) glow.style.opacity = ease(seg(progress, 0.12, 0.22)) * (1 - folderOut);

        const nextPoses = {};
        gradeCards.forEach((card, index) => {
          const start = 0.18 + (index * 0.062);
          const landing = ease(seg(progress, start, start + 0.3));
          card.style.opacity = landing;
          card.style.transform = landing >= 1
            ? 'none'
            : `translate3d(0, calc(4vh * ${1 - landing}), calc(-1100px * ${1 - landing})) rotateZ(calc(var(--sr) * ${1 - landing}))`;
          const team = GRADE_TEAMS[index];
          let pose = 'base';
          if (landing >= 0.98) pose = GRADE_CARD_CONFIG[team.slug].pose;
          if (team.manager === 'Kevin' && progress >= 0.91) pose = 'smug';
          nextPoses[team.manager] = pose;
        });
        applyGradePoses(nextPoses);
        const gridOut = seg(progress, 0.86, 1);
        if (grid) {
          grid.style.transform = `translateZ(${gridOut * 140}px) rotateX(${gridOut * -3}deg)`;
          grid.style.opacity = 1 - ease(seg(progress, 0.97, 1));
        }
      },
      exhibits(progress) {
        const beat = beats.current.exhibits;
        const stage = query('.fh-stage', beat);
        const copy = query('.fh-bureau__copy', beat);
        const copyIn = ease(seg(progress, 0, 0.12));
        if (copy) {
          copy.style.opacity = copyIn;
          copy.style.transform = `translateY(${lerp(14, 0, copyIn)}px)`;
        }
        if (!stage || !exhibitEls.length) return;
        const sweep = seg(progress, 0.18, 0.92);
        const index = Math.min(exhibitEls.length - 1, Math.floor(sweep * exhibitEls.length));
        stage.style.setProperty('--spot-x', `${((index + 0.5) / exhibitEls.length) * 100}%`);
        stage.style.setProperty('--spot-a', ease(seg(progress, 0.12, 0.2)) * (1 - ease(seg(progress, 0.95, 1))));
        exhibitEls.forEach((exhibit, exhibitIndex) => exhibit.classList.toggle('is-lit', exhibitIndex === index && progress > 0.16));
        stage.style.setProperty('--bd-s', lerp(1, 1.1, progress));
      },
      closeup(progress) {
        const beat = beats.current.closeup;
        const head = query('.fh-closeup__head', beat);
        const figure = query('.fh-closeup__figure', beat);
        const headIn = ease(seg(progress, 0, 0.12));
        if (head) {
          head.style.opacity = headIn;
          head.style.transform = `translateY(${lerp(14, 0, headIn)}px)`;
        }
        const lineProgress = seg(progress, 0.14, 0.92);
        const index = Math.min(CLOSEUP_LINES.length - 1, Math.floor(lineProgress * CLOSEUP_LINES.length));
        closeupLines.forEach((line, lineIndex) => {
          line.classList.toggle('is-lit', lineIndex === index && progress > 0.1);
          line.classList.toggle('is-past', lineIndex < index);
        });
        applyCloseupPose(CLOSEUP_LINES[index]?.pose || 'base');
        const figureIn = ease(seg(progress, 0.04, 0.18));
        const figureOut = ease(seg(progress, 0.94, 1));
        if (figure) {
          figure.style.opacity = figureIn * (1 - figureOut);
          figure.style.transform = `translateX(${lerp(-28, 0, figureIn)}px) scale(${lerp(0.98, 1.02, progress)})`;
        }
      },
      end(progress) {
        const beat = beats.current.end;
        const eyebrow = query('.fh-end__eyebrow', beat);
        const first = query('.fh-end__a', beat);
        const second = query('.fh-end__b', beat);
        const funnel = query('.fh-end__funnel', beat);
        const sub = query('.fh-end__url', beat);
        if (eyebrow) eyebrow.style.opacity = ease(seg(progress, 0.03, 0.18));
        const firstIn = ease(seg(progress, 0.1, 0.32));
        if (first) {
          first.style.opacity = firstIn;
          first.style.transform = `translateY(${lerp(20, 0, firstIn)}px)`;
        }
        const secondIn = ease(seg(progress, 0.3, 0.52));
        if (second) {
          second.style.opacity = secondIn;
          second.style.transform = `translateY(${lerp(20, 0, secondIn)}px)`;
        }
        if (funnel) funnel.style.opacity = ease(seg(progress, 0.5, 0.65));
        if (sub) sub.style.opacity = ease(seg(progress, 0.62, 0.78));
      },
    };

    const update = () => {
      ticking = false;
      const viewportHeight = window.innerHeight;
      Object.entries(beats.current).forEach(([name, element]) => {
        if (!element || !functions[name]) return;
        const rect = element.getBoundingClientRect();
        if (rect.bottom < -viewportHeight || rect.top > viewportHeight * 2) return;
        const total = rect.height - viewportHeight;
        functions[name](total > 0 ? clamp(-rect.top / total) : 0);
      });
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    const onResize = () => {
      measureBoard();
      onScroll();
    };

    measureBoard();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [reduced]);

  const setBeat = (name) => (element) => { beats.current[name] = element; };

  return (
    <section
      ref={root}
      className={`fh ${reduced ? 'fh--static' : ''}`}
      aria-label="The film"
      data-autoplay-duration={MOTION_TIMING.homeFilmDurationMs}
      data-autoplay-state={state}
    >
      {/* 1 · the room */}
      <div className="fh-beat fh-beat--room" ref={setBeat('room')} style={{ '--len': '220vh' }} data-testid="home-film-room">
        <div className="fh-stage">
          <img className="fh-room__bg" src="/images/film/room_2am_back.webp" alt="" aria-hidden="true" />
          <img className="fh-room__fore" src="/images/film/room_2am_fore.webp" alt="" aria-hidden="true" />
          <div className="fh-room__lamp" aria-hidden="true" />
          <div className="fh-room__grade" aria-hidden="true" />
          <div className="fh-open">
            <div className="fh-open__live"><span className="live-dot" aria-hidden="true" /><span>Filed Sunday, Aug 30 · Dallas, Texas · 2:07 a.m.</span></div>
            <span className="fh-open__rule" aria-hidden="true" />
            <h1 className="fh-open__mark">BRUINS<b>09</b></h1>
            <p className="fh-open__sub">{sentenceCase(numberWords(PICKS.length))} names filled the board in the dining room of a beautiful new build. {sentenceCase(numberWords(DRAFTED_PICKS.length))} were drafted and {numberWords(KEEPER_PICKS.length)} were priced as keepers. {sentenceCase(numberWords(TEAMS.filter((team) => team.tier === 'a').length))} left with an A.</p>
            <p className="fh-open__tag">The draft is over. The record is not.</p>
          </div>
          <p className="fh-open__cue" aria-hidden="true">Playing · scroll to take over<span>↓</span></p>
        </div>
      </div>

      {/* 2 · the board */}
      <div className="fh-beat fh-beat--board" ref={setBeat('board')} style={{ '--len': '400vh' }}>
        <div className="fh-stage">
          <div className="fh-backdrop" aria-hidden="true"><img src="/images/film/board_wall.webp" alt="" /></div>
          <div className="fh-board__copy">
            <p className="fh-eyebrow fh-eyebrow--red"><span className="live-dot" aria-hidden="true" />The board · {ROUND_NUMBERS.length} rounds · {DRAFTED_PICKS.length} drafted · {KEEPER_PICKS.length} keepers</p>
            <h2 className="fh-display">Every name,<br />in order.</h2>
            <p className="fh-dek">{TEAMS.length} teams, {ROUND_NUMBERS.length} rounds, {DRAFTED_PICKS.length} drafted picks, and {KEEPER_PICKS.length} keeper prices entered into the record. The board does not care who hosted.</p>
            <figure className="fh-dissent">
              {reduced ? (
                <img src="/videos/tape/coronation-cold-open-poster.webp" alt="Kyle ranks himself first while AJ reacts" />
              ) : (
                <video
                  ref={coronationVideoRef}
                  muted
                  loop
                  autoPlay
                  playsInline
                  preload="metadata"
                  poster="/videos/tape/coronation-cold-open-poster.webp"
                  aria-label="The coronation cold open: Kyle selects himself; AJ reacts"
                  data-testid="home-film-coronation-video"
                >
                  <source src="/videos/tape/coronation-cold-open-480p.webm" type="video/webm" />
                  <source src="/videos/tape/coronation-cold-open-480p.mp4" type="video/mp4" />
                </video>
              )}
              <figcaption><b>Prior testimony · The Tape</b><span>He ranked himself first at 50.01 percent. He then drafted a B-plus. The Bureau records both.</span></figcaption>
            </figure>
          </div>
          <div className="fh-board__scene">
            <div className="fh-board-wall">
              {ROUND_COLUMNS.map((column, columnIndex) => (
                <div className="fh-board__column" key={columnIndex}>
                  {column.map((group) => (
                    <section className="fh-round" key={group.round} aria-labelledby={`film-round-${group.round}`}>
                      <h3 id={`film-round-${group.round}`}>Round {String(group.round).padStart(2, '0')}</h3>
                      <ol className="fh-round__picks">
                        {group.picks.map((pick) => {
                          const annotation = boardAnnotation(pick);
                          return (
                            <li
                              key={pick.overall}
                              className={`fh-slip ${pick.keeper ? 'is-keeper' : ''} ${ANNOTATED_PICKS.has(pick.overall) ? 'is-annotated' : ''}`}
                              data-column={columnIndex}
                            >
                              <span className="fh-slip__pick">R{pick.round} · {pick.overall}</span>
                              <b>{pick.player}</b>
                              <span className="fh-slip__pos">{pick.position}</span>
                              <span className="fh-slip__team">{pick.fantasyTeam}</span>
                              {annotation && <em className="fh-slip__annotation">{annotation}</em>}
                            </li>
                          );
                        })}
                      </ol>
                    </section>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 3 · the grades */}
      <div className="fh-beat fh-beat--grades" ref={setBeat('grades')} style={{ '--len': '400vh' }}>
        <div className="fh-stage">
          <div className="fh-grades__head">
            <p className="fh-eyebrow">The grades · {Object.keys(draftGrades.methodology.weights).length} inputs · {TEAMS.length} teams</p>
            <h2 className="fh-display">The grades are in.</h2>
            <p className="fh-dek">Starter strength, total VORP, drafted efficiency, keeper value, bench quality, bye resilience, and positional construction. Nobody voted. The ledger did.</p>
          </div>
          <div className="fh-grades__scene">
            <div className="fh-folder__glow" aria-hidden="true" />
            <div className="fh-folder" aria-hidden="true">
              <img className="fh-folder__base" src="/images/film/folder_base.webp" alt="" />
              <div className="fh-folder__flap"><img src="/images/film/folder_base.webp" alt="" /></div>
            </div>
            <ul className="fh-grades__grid">
              {GRADE_TEAMS.map((team) => <GradeCard key={team.slug} team={team} pose={gradePoses[team.manager] || 'base'} />)}
            </ul>
          </div>
        </div>
      </div>

      {/* 4 · the exhibits */}
      <div className="fh-beat fh-beat--exhibits" ref={setBeat('exhibits')} style={{ '--len': '300vh' }}>
        <div className="fh-stage">
          <div className="fh-backdrop fh-backdrop--court" aria-hidden="true"><img src="/images/film/courtroom.webp" alt="" /></div>
          <div className="fh-spot" aria-hidden="true" />
          <div className="fh-bureau__copy">
            <p className="fh-eyebrow fh-eyebrow--red">Vol XIV · Department of the Draft</p>
            <h2 className="fh-display">The Bureau files<br /><span className="fh-red">{numberWords(EXHIBITS.length)} exhibits.</span></h2>
            <p className="fh-dek">Thirteen seasons, 875 regular-season games, and one draft. All four findings below are checkable.</p>
          </div>
          <ul className="fh-dock">
            {EXHIBITS.map((exhibit) => (
              <li key={exhibit.tag} className="fh-exhibit">
                <div className="fh-exhibit__fig paper"><CastFigure name={exhibit.name} trigger="none" pose={exhibit.pose} alt={`${exhibit.name} caricature`} /></div>
                <p className="fh-exhibit__tag">{exhibit.tag}</p>
                <p className="fh-exhibit__name">{exhibit.name === 'A.J.' ? 'AJ' : exhibit.name}</p>
                <p className={`fh-exhibit__num ${exhibit.tone === 'red' ? 'fh-red' : 'fh-gold'}`}>{exhibit.num}</p>
                <p className="fh-exhibit__copy">{exhibit.copy}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* 5 · close-up */}
      <div className="fh-beat fh-beat--closeup" ref={setBeat('closeup')} style={{ '--len': '340vh' }}>
        <div className="fh-stage">
          <div className="fh-closeup__head">
            <p className="fh-eyebrow fh-eyebrow--red">Close-up · Highland Park Hitmen · Grade {AJ.letter}</p>
            <p className="fh-closeup__register">The venue was immaculate. The Bureau has no notes on the house.</p>
          </div>
          <CastFigure name="A.J." trigger="none" pose={closeupPose} className="fh-closeup__figure" alt="A.J. caricature" />
          <ol className="fh-closeup__lines">
            {CLOSEUP_LINES.map((line) => (
              <li className="fh-closeup__line" key={line.big}>
                <strong>{line.big}</strong>
                <span>{line.small}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* 6 · end card */}
      <div className="fh-beat fh-beat--end" ref={setBeat('end')} style={{ '--len': '180vh' }}>
        <div className="fh-stage">
          {reduced ? (
            <img className="fh-end__video" src="/videos/tape/aj-empty-stadium-hero-poster.webp" alt="" aria-hidden="true" />
          ) : (
            <video ref={endVideoRef} className="fh-end__video" muted loop autoPlay playsInline preload="metadata" poster="/videos/tape/aj-empty-stadium-hero-poster.webp" aria-hidden="true" tabIndex="-1">
              <source src="/videos/tape/aj-empty-stadium-hero-480p.webm" type="video/webm" />
              <source src="/videos/tape/aj-empty-stadium-hero-480p.mp4" type="video/mp4" />
            </video>
          )}
          <div className="fh-end__shade" aria-hidden="true" />
          <div className="fh-end">
            <p className="fh-end__eyebrow">{endCard.eyebrow}</p>
            <p className="fh-end__a">{endCard.a}</p>
            <p className="fh-end__b">{endCard.b}</p>
            <Link className="fh-end__funnel" to="/draft-grades">All {numberWords(TEAMS.length)} grades, with the math →</Link>
            <p className="fh-end__url"><span className="live-dot" aria-hidden="true" />Kickoff Thursday, Sept 10 · The almanac continues below</p>
          </div>
        </div>
      </div>

      <div className="fh-controls">
          <Link className="fh-btn fh-btn--grades" to="/draft-grades">Grades →</Link>
          {!reduced && <button type="button" className="fh-btn" onClick={skip}>Skip the film</button>}
          {!reduced && state !== 'done' && (
            <button type="button" className="fh-btn fh-btn--play" data-state={state} onClick={() => (state === 'playing' ? pause() : play())} aria-label={state === 'playing' ? 'Pause' : 'Play'}>
              <span className="fh-btn__icon" aria-hidden="true" />{state === 'idle' ? 'Starting…' : state === 'playing' ? 'Pause' : 'Play'}
            </button>
          )}
        </div>
    </section>
  );
}
